import { call, put, select, all, take } from 'redux-saga/effects';
import * as actions from './actions';
import { Base64 } from 'js-base64';
import axios from 'axios';
import { actions as campaignActions } from 'store/campaign';
import { actions as userMessageActions } from 'store/userMessaging';
import { types } from 'constants.js';
import { waitFor } from 'store/helpers';
import * as eventSagas from 'store/event/sagas';
import { AUTO_SAVE_TIMER_MS } from 'constants.js';
import { updateAttachmentFilters } from './actions';
import { getParams, safeBase64Encode, safeBase64Decode } from 'store/helpers/helperFunctions';
const { waitForGetSupportedEventTypes, waitForGetEventMetaData } = eventSagas;

export function* waitForGetAttachmentsTemplates() {
  return yield waitFor(
    actions.getAttachmentTemplates(),
    actions.GETTING_ATTACHMENT_TEMPLATES_SUCCEEDED,
    actions.GETTING_ATTACHMENT_TEMPLATES_FAILED
  );
}

// hydration and shape helpers
const decodeTemplateContent = template => {
  template.jsonContent = safeBase64Decode(template.jsonContent);
  template.content = safeBase64Decode(template.content);
  return template;
};

const encodeTemplateContent = template => {
  if (template.content) {
    template.content = safeBase64Encode(template.content, true);
  } else {
    template.content = safeBase64Encode(template.jsonContent.html, true);
  }
  template.jsonContent = safeBase64Encode(template.jsonContent);
  return template;
};

const setCampaignOccurrenceType = template => {
  if (!template.hasOwnProperty('campaignOccurrenceType')) {
    if (template.schemaId === 'static_scheduled') {
      template.campaignOccurrenceType = 'SCHEDULED';
    } else if (template.schemaId === 'static_onDemand') {
      template.campaignOccurrenceType = 'ON_DEMAND';
    } else {
      template.campaignOccurrenceType = 'ON_EVENT';
    }
  }

  return template;
};

// the actual http calls
const apiRoot = `${window._env_?.REACT_APP_API_PATH}`;

const getTemplateApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/attachment-template`;

const getTagsApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/tags/attachment-template`;

let isArchiveInProgress = false;

export const getPaginatedTemplatesAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getMinimalAttachmentTemplatesAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/advance-search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getTemplatesAsync = (tenantId, businessContextId, templateUuid) =>
  axios
    .get(
      `${getTemplateApiRoot(tenantId, businessContextId)}${templateUuid ? `/uuid/${templateUuid}` : ''
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getAttachmentTemplateDetailsAsync = (
  tenantId,
  businessContextId,
  attachmentTemplateUuids
) => {
  return axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/uuid/bulk`,
      attachmentTemplateUuids
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

//deprecated
export const getTemplatesWithIdAsync = (tenantId, businessContextId, templateId) =>
  axios
    .get(
      `${getTemplateApiRoot(tenantId, businessContextId)}${
        templateId ? `/${templateId}` : ''
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getTagsAsync = (tenantId, businessContextId) =>
  axios
    .get(`${getTagsApiRoot(tenantId, businessContextId)}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const createTemplateAsync = (tenantId, businessContextId, template) =>
  axios
    .post(`${getTemplateApiRoot(tenantId, businessContextId)}`, template)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateTemplateAsync = (tenantId, businessContextId, template) =>
  axios
    .put(
      `${getTemplateApiRoot(tenantId, businessContextId)}/uuid/${template.uuid}`,
      template
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

//deprecated
export const updateTemplateWithIdAsync = (tenantId, businessContextId, template) =>
  axios
    .put(
      `${getTemplateApiRoot(tenantId, businessContextId)}/${template.id}`,
      template
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteTemplateAsync = (tenantId, businessContextId, templateUuid) =>
  axios
    .delete(`${getTemplateApiRoot(tenantId, businessContextId)}/uuid/${templateUuid}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

//deprecated
export const deleteTemplateWithIdAsync = (tenantId, businessContextId, templateId) =>
  axios
    .delete(`${getTemplateApiRoot(tenantId, businessContextId)}/${templateId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

/**
 * Test attachment template API call (matches print template pattern)
 * Returns a PDF blob on success
 * Note: payload should already be encoded via encodeTemplateContent in useTemplate.js
 */
export const getSampleAsync = (tenantId, businessContextId, payload) =>
  axios({
    url: `${getTemplateApiRoot(tenantId, businessContextId)}/verify`,
    method: 'POST',
    responseType: 'blob',
    data: payload
  })
  .then(response => response.data)
  .catch(err => {
    throw err;
  });

// sagas

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const currentTemplate = store =>
  store.attachmentTemplate ? store.attachmentTemplate.current : {};
const templateList = store =>
  store.attachmentTemplate ? store.attachmentTemplate.list : [];
const paginatedTemplateList = store =>
  store.attachmentTemplate ? store.attachmentTemplate.paginatedList : [];
const minimalTemplateList = store =>
  store.attachmentTemplate ? store.attachmentTemplate.minimalList : [];
const minimalTotalRecords = store =>
  store.attachmentTemplate ? store.attachmentTemplate.minimalTotalRecords : 0;
const productNames = store => store.events.supportedEventTypes.productNames;
const productCategories = store =>
  store.events.supportedEventTypes.productCategories;
const tags = store =>
  store.attachmentTemplate ? store.attachmentTemplate.tagList : [];
const searchInput = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.searchInput : '';
const displayActive = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.displayActive : true;
const sortBy = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.sortBy : 'name';
const sortOrder = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.rowsPerPage : 10;
const pageNo = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.pageNo : 0;
const totalCount = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.totalCount : 0;
const filterTypes = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.filterTypes : [];
const filterTemplates = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.filterTemplates : [];
const filterProductNames = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.filterProductNames : [];
const filterProductCategories = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.filterProductCategories : [];
const filterMarketingTypes = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.filterMarketingTypes : [];
const filterTags = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.filterTags : [];
const filterStatus = store =>
  store.attachmentTemplate.filters ? store.attachmentTemplate.filters?.filterStatus : [];
const selectedAttachmentNamesByUuid = store =>
  store.attachmentTemplate ? store.attachmentTemplate.selectedNamesByUuid : {};
// helper saga to prevent culling the saga tree on rejection of a parallelized task.
function* reflect(callArgs) {
  try {
    return { value: yield call(...callArgs), status: 'resolved' };
  } catch (ex) {
    return ex;
  }
}

export function* getTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(templateList);
    if (
      action.type === actions.GET_ATTACHMENT_TEMPLATES &&
      (currentList.length === 0 || payload.force === true)
    ) {
      yield put(actions.gettingAttachmentTemplates());
      yield call(waitForGetSupportedEventTypes);
      currentList = yield call(getTemplatesAsync, tenantId, businessContextId);
      currentList = currentList.map(decodeTemplateContent);
      currentList = currentList.map(setCampaignOccurrenceType);
    }

    yield put(actions.gettingAttachmentTemplatesSucceeded(currentList));
  } catch (ex) {
    yield put(actions.gettingAttachmentTemplatesFailed(ex));
  }
}

export function* getPaginatedTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedTemplateList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);
    if (
      action.type === actions.GET_PAGINATED_ATTACHMENT_TEMPLATES &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const campaignContext = payload.campaignContext || {};
      const hasCampaignContext = !!payload.campaignContext;
      const selectedStatusFilters = yield select(filterStatus);
      const activeValueFromStatusFilters =
        Array.isArray(selectedStatusFilters) && selectedStatusFilters.length === 1
          ? (selectedStatusFilters.includes('active')
              ? true
              : (selectedStatusFilters.includes('archived') ? false : null))
          : null;
      const requestBody = {
        name: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        type: yield select(filterTypes),
        searchScopes: yield select(filterTemplates),
        tags: yield select(filterTags),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        ...(hasCampaignContext
          ? (activeValueFromStatusFilters !== null
              ? { active: activeValueFromStatusFilters }
              : {})
          : { active: yield select(displayActive) }),
        ...(campaignContext.productType && {
          productType: campaignContext.productType
        }),
        ...(campaignContext.subscriberListId && {
          subscriberListId: campaignContext.subscriberListId
        }),
        ...(campaignContext.segmentedListId && {
          segmentedListId: campaignContext.segmentedListId
        }),
        ...(campaignContext.eventTypeCategory && {
          eventTypeCategory: Array.isArray(campaignContext.eventTypeCategory)
            ? campaignContext.eventTypeCategory
            : [campaignContext.eventTypeCategory]
        }),
        promotional:
          typeof campaignContext.promotional === 'boolean'
            ? campaignContext.promotional
            : yield select(filterMarketingTypes),
        ...(hasCampaignContext && {
          surveyResponseTemplate:
            campaignContext.campaignOccurrenceType === 'INTERNAL_SURVEY_RESPONSE' ||
            campaignContext.campaignOccurrenceType === 'EXTERNAL_SURVEY_RESPONSE'
              ? true
              : false
        })
      };
      let urlParams = {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = {
        ...getParams(requestBody, productName, productCategory)
      };
      let listCount = yield select(totalCount);
      if (
        isArchiveInProgress &&
        listCount - 1 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(updateAttachmentFilters({ pageNo: urlParams.index }));
      }
      yield put(actions.gettingPaginatedAttachmentTemplates());
      currentList = yield call(
        getPaginatedTemplatesAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      yield put(updateAttachmentFilters({ totalCount: currentList.totalRecords }));
      currentList = currentList.data.map(decodeTemplateContent);
      currentList = currentList.map(setCampaignOccurrenceType);
    }
    isArchiveInProgress = false;
    yield put(
      actions.gettingPaginatedAttachmentTemplatesSucceeded(currentList)
    );
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(updateAttachmentFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedAttachmentTemplatesFailed(ex));
  }
}

export function* getMinimalAttachmentTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = payload.businessContextId || (yield select(currentBusinessContextId));
    
    if (action.type === actions.GET_MINIMAL_ATTACHMENT_TEMPLATES) {
      yield put(actions.gettingMinimalAttachmentTemplates());
      
      let urlParams = {
        index: payload.index || 0,
        pageSize: payload.pageSize || 100
      };
      
      let bodyParams = {
        active: payload.active !== undefined ? payload.active : true,
        requiredFields: payload.requiredFields || [],
        ...(payload.productCategory && { productCategory: payload.productCategory }),
        ...(payload.productName && { productName: payload.productName }),
        ...(payload.eventTypeCategory && { eventTypeCategory: payload.eventTypeCategory }),
        ...(payload.name && { name: payload.name }),
        ...(payload.type && { type: payload.type })
      };
      
      let currentList = yield call(
        getMinimalAttachmentTemplatesAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      
      yield put(actions.gettingMinimalAttachmentTemplatesSucceeded({
        data: currentList.data,
        totalRecords: currentList.totalRecords
      }));
    }
  } catch (ex) {
    yield put(actions.gettingMinimalAttachmentTemplatesFailed(ex));
  }
}

export function* getAttachmentTemplateNamesByUuidsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId =
      payload.businessContextId || (yield select(currentBusinessContextId));
    const existingNamesByUuid = yield select(selectedAttachmentNamesByUuid);

    const uniqueUuids = Array.from(
      new Set((payload.templateUuids || []).filter(Boolean))
    );
    const missingUuids = uniqueUuids.filter(
      uuid => !existingNamesByUuid?.[uuid]
    );

    if (missingUuids.length === 0) {
      yield put(actions.gettingAttachmentTemplateNamesByUuidsSucceeded({}));
      return;
    }

    yield put(actions.gettingAttachmentTemplateNamesByUuids());

    const templateResponses = yield call(
      getAttachmentTemplateDetailsAsync,
      tenantId,
      businessContextId,
      missingUuids
    );

    const namesByUuid = (templateResponses || []).reduce((result, template) => {
      if (template?.uuid && template?.name) {
        result[template.uuid] = template.name;
      }
      return result;
    }, {});

    yield put(actions.gettingAttachmentTemplateNamesByUuidsSucceeded(namesByUuid));
  } catch (ex) {
    yield put(actions.gettingAttachmentTemplateNamesByUuidsFailed(ex));
  }
}

export function* getTagsIfNeeded(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let tagList = yield select(tags);

    if (action.type === actions.GET_ATTACHMENT_TAGS) {
      yield put(actions.gettingAttachmentTags());
      tagList = yield call(getTagsAsync, tenantId, businessContextId);
    }
    yield put(actions.gettingAttachmentTagsSucceeded(tagList));
  } catch (ex) {
    yield put(actions.gettingAttachmentTagsFailed(ex));
  }
}

export function* getTemplateIfNeeded(action) {
  const { payload = {} } = action;
  try {
    yield put(actions.gettingAttachmentTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = payload.businessContextId || (yield select(currentBusinessContextId));
    let template = yield select(currentTemplate);
    let templateUuid = payload.templateUuid;

    let yieldAlls = {
      supportedEventTypes: yield call(waitForGetSupportedEventTypes)
    };

    // Guard: If no templateUuid provided, use existing template and skip API call
    if (!templateUuid) {
      let eventMetaData;
      if (template.schemaIdentifier  && !payload.skipMetadata) {
        yieldAlls.eventMetaData = yield call(waitForGetEventMetaData, {
          campaignOccurrenceType: template.campaignOccurrenceType,
          schemaIdentifier: template.schemaIdentifier,
          productName: template.productName,
          workflow: 'template',
          productType: template.productType,
          languageCode: template?.languageCode || null
        });
        ({ eventMetaData: { eventMetaData } = {} } = yield all(yieldAlls));
      }

      if (!template.hasOwnProperty('promotional')) {
        template = { ...template, promotional: false };
      }

      yield put(
        actions.gettingAttachmentTemplateSucceeded({
          template,
          eventMetaData
        })
      );
      return;
    }

    if ((template.uuid !== templateUuid && !(template._isCopy || template._isCopyTemplate)) || payload.force === true) {
      template = yield call(
        getTemplatesAsync,
        tenantId,
        businessContextId,
        templateUuid
      );

      template = decodeTemplateContent(template);
      template = setCampaignOccurrenceType(template);
    }

    let eventMetaData;
    if (template.schemaIdentifier && template.campaignOccurrenceType  && !payload.skipMetadata) {
      yieldAlls.eventMetaData = yield call(waitForGetEventMetaData, {
        campaignOccurrenceType: template.campaignOccurrenceType,
        schemaIdentifier: template.schemaIdentifier,
        productName: template.productName,
        workflow: 'template',
        productType: template.productType,
        languageCode: template?.languageCode || null
      });
      ({ eventMetaData: { eventMetaData } = {} } = yield all(yieldAlls));
    }

    if (!template.hasOwnProperty('promotional')) {
      template = { ...template, promotional: false };
    }

    yield put(
      actions.gettingAttachmentTemplateSucceeded({
        template,
        eventMetaData
      })
    );
  } catch (ex) {
    yield put(actions.gettingAttachmentTemplateFailed(ex));
  }
}

export function* createTemplateFromCurrent() {
  try {
    yield put(actions.creatingAttachmentTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let eventMetaData = template.eventMetaData;
    const isCopyFlow = !!(template._isCopy || template._isCopyTemplate);
    if (isCopyFlow) {
      template = { ...template };
      delete template.id;
      delete template.uuid;
    }

    if (!template.uuid) {
      template = encodeTemplateContent({ ...template });

      if (template.type == 'SMS' && template.hasOwnProperty('layout')) {
        delete template.layout;
      }

      template = yield call(
        createTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }
    template = decodeTemplateContent(template);
    yield put(
      actions.creatingAttachmentTemplateSucceeded({
        ...template,
        eventMetaData: eventMetaData
      })
    );
  } catch (ex) {
    yield put(actions.creatingAttachmentTemplateFailed(ex));
  }
}

export function* updateTemplateFromCurrent() {
  try {
    yield put(actions.updatingAttachmentTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let eventMetaData = template.eventMetaData;

    if (template.uuid) {
      template = encodeTemplateContent({ ...template });
      template = yield call(
        updateTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }
    template = decodeTemplateContent(template);
    yield put(
      actions.updatingAttachmentTemplateSucceeded({
        ...template,
        eventMetaData: eventMetaData
      })
    );
  } catch (ex) {
    yield put(actions.updatingAttachmentTemplateFailed(ex));
  }
}

export function* warnAndArchive(action) {
  yield put(
    campaignActions.getAssociatedCampaignsFor({
      uuid: action.payload.uuid,
      typeOf: types.ATTACHMENT_TEMPLATE
    })
  );
  yield take(
    actions.GETTING_ASSOCIATED_CAMPAIGNS_FOR_ATTACHMENT_TEMPLATE_SUCCEEDED
  );
  let templates = yield select(paginatedTemplateList);
  let templateToWarnAbout = templates.find(
    template => template.uuid === action.payload.uuid
  );
  action.payload.message.props = {
    ...action.payload.message.props,
    template: templateToWarnAbout,
    type: 'attachment'
  };
  yield put(userMessageActions.setMessage(action.payload.message));
}

export function* deleteTemplatesByIdList(action) {
  const { payload } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let templateUuids = payload.templateUuids;
    let templates = yield select(paginatedTemplateList);

    let deletedTemplateStatuses = [];
    if (templateUuids.length > 0) {
      yield put(actions.deletingAttachmentTemplates());
      // all() prallelizes delete operations and waits until they all finish.
      // if the delete template API method can be made to return the deleted id this could be set to a variable and report partial success which would make for better failure handling.
      if (payload.hardDelete) {
        deletedTemplateStatuses = yield all(
          templateUuids
            .map(templateUuid => [
              deleteTemplateAsync,
              tenantId,
              businessContextId,
              templateUuid
            ])
            .map(reflect)
        );
      } else {
        deletedTemplateStatuses = yield all(
          templateUuids
            .map(templateUuid => {
              const template = templates.find(t => t.uuid === templateUuid);
              const encodedTemplate = encodeTemplateContent({ ...template });
              return [
                updateTemplateAsync,
                tenantId,
                businessContextId,
                {
                  ...encodedTemplate,
                  active: !payload.activeStatus
                }
              ];
            })
            .map(reflect)
        );
      }
    }
    if (
      deletedTemplateStatuses.filter(status => status.status === 'resolved')
        .length === templateUuids.length
    ) {
      isArchiveInProgress = true;
      yield all([
        put(
          actions.deletingAttachmentTemplatesSucceeded(
            templateUuids,
            payload.hardDelete
          )
        ),
        put(actions.getPaginatedAttachmentTemplates({ force: true }))
      ]);
    } else {
      yield put(
        actions.deletingAttachmentTemplatesFailed({
          message: 'Not all requested templates could be deleted.',
          deletedTemplateStatuses
        })
      );
    }
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(
      actions.deletingAttachmentTemplatesFailed({
        ...ex,
        hardDelete: payload.hardDelete
      })
    );
  }
}

let timer;
export function* autoSaveTimerStart() {
  if (timer) {
    clearTimeout(timer);
  }
  yield call(
    () =>
      new Promise(resolve => {
        timer = setTimeout(resolve, AUTO_SAVE_TIMER_MS);
      })
  );

  yield put(actions.attachmentAutoSaveTimerEnd());
}

export function* getAttachmentTemplateFile(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let response = yield call(
      getSampleAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield put(actions.gettingAttachmentTemplateFileSucceeded(response));
  } catch (ex) {
    yield put(actions.gettingAttachmentTemplateFileFailed(ex));
  }
}
